import type { UTCTimestamp, LineData } from "lightweight-charts";
import type { LinePoint } from "../types.js";

export type LineField = "call" | "put" | "call0dte" | "put0dte";

/**
 * Project the cumulative line series onto one field, in $millions, as
 * lightweight-charts LineData. Time is epoch SECONDS (point.t ms / 1000).
 * Points with a non-finite/non-positive time or a non-finite value are
 * dropped; the result is sorted ascending and de-duplicated by second (the
 * last value for a second wins) so lightweight-charts' setData never sees
 * unordered or duplicate times (its production build silently mis-indexes
 * unordered data — see GammaLens's chart wrapper).
 */
export function toLineData(points: LinePoint[], field: LineField): LineData[] {
  const mapped = points
    .filter((pt) => Number.isFinite(pt.t) && pt.t > 0 && Number.isFinite(pt[field]))
    .map((pt) => ({ time: Math.floor(pt.t / 1000) as UTCTimestamp, value: pt[field] / 1_000_000 }))
    .sort((a, b) => (a.time as number) - (b.time as number));
  return dedupeBySecond(mapped);
}

/**
 * Project the spot-price overlay from the same line series, unscaled (dollars,
 * not $M). Points without a usable spot (null/absent/non-finite) are dropped,
 * so a partially-spotless day renders the price line only where spot existed.
 */
export function toSpotData(points: LinePoint[]): LineData[] {
  const mapped = points
    .filter((pt) => Number.isFinite(pt.t) && pt.t > 0 && typeof pt.spot === "number" && Number.isFinite(pt.spot) && pt.spot > 0)
    .map((pt) => ({ time: Math.floor(pt.t / 1000) as UTCTimestamp, value: pt.spot as number }))
    .sort((a, b) => (a.time as number) - (b.time as number));
  return dedupeBySecond(mapped);
}

function dedupeBySecond(sorted: LineData[]): LineData[] {
  return sorted.filter((d, i, arr) => {
    const next = arr[i + 1];
    return next === undefined || (next.time as number) !== (d.time as number);
  });
}
